// Client-side logging utility for debugging user issues
// Logs client-side events with session ID and browser information

import { sessionManager } from './sessionManager';
import { detectMobileBrowser } from './browserDetector';

const LOG_LEVELS = {
    DEBUG: 'debug',
    INFO: 'info',
    WARN: 'warn',
    ERROR: 'error'
};

// Configuration
const LOG_CONFIG = {
    enabled: true,
    logToConsole: true,
    logToServer: false, // Can be enabled to send critical errors to server
    maxLogEntries: 100, // Keep last 100 log entries in memory
    serverEndpoint: '/api/logs/client' // Endpoint for sending logs to server
};

// In-memory log storage
let logEntries = [];

/**
 * Gets browser and device information for logging context
 */
function getLogContext() {
    const browserInfo = detectMobileBrowser();
    const sessionId = sessionManager.getSession();
    
    return {
        sessionId: sessionId,
        userAgent: navigator.userAgent,
        browserType: browserInfo.browserType,
        isMobile: browserInfo.isMobile,
        platform: navigator.platform,
        language: navigator.language,
        timestamp: new Date().toISOString(),
        url: window.location.href
    };
}

/**
 * Creates a log entry
 */
function createLogEntry(level, message, data = null, error = null) {
    const context = getLogContext();
    const entry = {
        level: level,
        message: message,
        data: data,
        error: error ? {
            message: error.message,
            stack: error.stack,
            name: error.name
        } : null,
        context: context,
        timestamp: Date.now()
    };
    
    return entry;
}

/**
 * Adds log entry to in-memory storage
 */
function addToLogStorage(entry) {
    logEntries.push(entry);
    
    // Keep only last maxLogEntries
    if (logEntries.length > LOG_CONFIG.maxLogEntries) {
        logEntries.shift();
    }
}

/**
 * Sends critical errors to server (if enabled)
 */
async function sendToServer(entry) {
    if (!LOG_CONFIG.logToServer || entry.level !== LOG_LEVELS.ERROR) {
        return;
    }
    
    try {
        // Only send critical errors to reduce server load
        await fetch(LOG_CONFIG.serverEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(entry)
        });
    } catch (error) {
        // Silently fail - don't break app if logging fails
        console.warn('Failed to send log to server:', error);
    }
}

/**
 * Logs a debug message
 */
export function debug(message, data = null) {
    if (!LOG_CONFIG.enabled) return;
    
    const entry = createLogEntry(LOG_LEVELS.DEBUG, message, data);
    addToLogStorage(entry);
    
    if (LOG_CONFIG.logToConsole) {
        console.debug('[Logger]', message, data || '');
    }
}

/**
 * Logs an info message
 */
export function info(message, data = null) {
    if (!LOG_CONFIG.enabled) return;
    
    const entry = createLogEntry(LOG_LEVELS.INFO, message, data);
    addToLogStorage(entry);
    
    if (LOG_CONFIG.logToConsole) {
        console.info('[Logger]', message, data || '');
    }
}

/**
 * Logs a warning message
 */
export function warn(message, data = null) {
    if (!LOG_CONFIG.enabled) return;
    
    const entry = createLogEntry(LOG_LEVELS.WARN, message, data);
    addToLogStorage(entry);
    
    if (LOG_CONFIG.logToConsole) {
        console.warn('[Logger]', message, data || '');
    }
}

/**
 * Logs an error message
 */
export function error(message, errorObj = null, data = null) {
    if (!LOG_CONFIG.enabled) return;
    
    const entry = createLogEntry(LOG_LEVELS.ERROR, message, data, errorObj);
    addToLogStorage(entry);
    
    if (LOG_CONFIG.logToConsole) {
        console.error('[Logger]', message, errorObj || data || '');
    }
    
    // Send critical errors to server
    sendToServer(entry);
}

/**
 * Logs Vipps authentication flow events
 */
export function logVippsEvent(eventType, data = null) {
    info(`Vipps ${eventType}`, {
        eventType: eventType,
        ...data
    });
}

/**
 * Logs state transitions
 */
export function logStateTransition(fromState, toState, data = null) {
    debug(`State transition: ${fromState} -> ${toState}`, data);
}

/**
 * Logs API call
 */
export function logApiCall(method, url, data = null) {
    debug(`API ${method} ${url}`, data);
}

/**
 * Logs API response
 */
export function logApiResponse(method, url, status, data = null) {
    if (status >= 400) {
        warn(`API ${method} ${url} returned ${status}`, data);
    } else {
        debug(`API ${method} ${url} returned ${status}`, data);
    }
}

/**
 * Gets all log entries
 */
export function getLogEntries(level = null, limit = null) {
    let entries = logEntries;
    
    if (level) {
        entries = entries.filter(e => e.level === level);
    }
    
    if (limit) {
        entries = entries.slice(-limit);
    }
    
    return entries;
}

/**
 * Clears log entries
 */
export function clearLogs() {
    logEntries = [];
}

/**
 * Exports logs as JSON string
 */
export function exportLogs() {
    return JSON.stringify(logEntries, null, 2);
}

export const logger = {
    debug,
    info,
    warn,
    error,
    logVippsEvent,
    logStateTransition,
    logApiCall,
    logApiResponse,
    getLogEntries,
    clearLogs,
    exportLogs
};

